import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["display", "element", "presetButton", "customButton", "trigger", "content"]
  static values = {
    fromDate: String,
    toDate: String,
    redirectPath: String,
    months: { type: Number, default: 2 },
    additionalParams: { type: Object, default: {} }
  }

  connect() {
    this.updateDisplay()
    this.highlightActiveOption()

    // calendar-range要素の値を初期設定
    if (this.hasElementTarget) {
      const value = `${this.fromDateValue}/${this.toDateValue}`
      this.setCalendarValue(value, false)
      this.handleCalendarChangeBound = this.handleCalendarChange.bind(this)
      this.elementTarget.addEventListener('change', this.handleCalendarChangeBound)
      this.elementTarget.addEventListener('input', this.handleCalendarChangeBound)
    }
  }

  disconnect() {
    if (this.hasElementTarget && this.handleCalendarChangeBound) {
      this.elementTarget.removeEventListener('change', this.handleCalendarChangeBound)
      this.elementTarget.removeEventListener('input', this.handleCalendarChangeBound)
    }
  }

  updateDisplay() {
    if (!this.hasDisplayTarget) return
    
    try {
      const fromDate = new Date(this.fromDateValue)
      const toDate = new Date(this.toDateValue)
      
      const formatDate = (date) => {
        const year = date.getFullYear()
        const month = (date.getMonth() + 1).toString().padStart(2, '0')
        const day = date.getDate().toString().padStart(2, '0')
        return `${year}/${month}/${day}`
      }
      
      this.displayTarget.textContent = `${formatDate(fromDate)} - ${formatDate(toDate)}`
    } catch (error) {
      console.error('Date formatting error:', error)
      this.displayTarget.textContent = `${this.fromDateValue} - ${this.toDateValue}`
    }
    this.highlightActiveOption()
  }

  closeCalendar(event) {
    if (event) {
      event.preventDefault()
      event.stopPropagation()
      event.currentTarget?.blur()
    }
    // dropdownを閉じる
    if (this.hasTriggerTarget) {
      setTimeout(() => this.triggerTarget.blur(), 0)
    }
    document.activeElement?.blur?.()
    this.element.classList.remove('dropdown-open')
    if (this.hasContentTarget) {
      this.contentTarget.classList.remove('dropdown-open')
    }
  }

  applyDateRange() {
    if (!this.hasElementTarget) return

    try {
      const calendarValue = this.elementTarget.value
      if (!calendarValue || !calendarValue.includes('/')) {
        console.warn('Invalid calendar value:', calendarValue)
        return
      }

      const [fromDate, toDate] = calendarValue.split('/')

      // 日付範囲の検証（最大400日）
      const from = new Date(fromDate)
      const to = new Date(toDate)
      const diffTime = Math.abs(to - from)
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24))

      if (diffDays > 400) {
        alert('日付範囲は最大400日までです。')
        return
      }

      if (this.redirectPathValue && fromDate && toDate) {
        // URLに日付パラメータを付与してリダイレクト
        const url = new URL(this.redirectPathValue, window.location.origin)
        url.searchParams.set('fromDate', fromDate)
        url.searchParams.set('toDate', toDate)

        // 追加パラメータを引き継ぐ
        if (this.additionalParamsValue && typeof this.additionalParamsValue === 'object') {
          Object.entries(this.additionalParamsValue).forEach(([key, value]) => {
            if (value !== null && value !== undefined && value !== '') {
              url.searchParams.set(key, value)
            }
          })
        }

        window.location.href = url.toString()
      } else {
        console.warn('Redirect path or dates missing:', {
          redirectPath: this.redirectPathValue,
          fromDate,
          toDate
        })
      }
    } catch (error) {
      console.error('Error applying date range:', error)
    }

    this.closeCalendar()
  }

  // 値が更新された時の処理
  fromDateValueChanged() {
    this.updateDisplay()
    this.updateCalendarValue()
    this.highlightActiveOption()
  }

  toDateValueChanged() {
    this.updateDisplay() 
    this.updateCalendarValue()
    this.highlightActiveOption()
  }

  updateCalendarValue() {
    if (this.hasElementTarget) {
      const value = `${this.fromDateValue}/${this.toDateValue}`
      this.setCalendarValue(value, true)
    }
  }

  // プリセット期間選択
  selectPreset(event) {
    const preset = event.currentTarget.dataset.preset
    const { fromDate, toDate } = this.calculatePresetDates(preset)

    this.fromDateValue = fromDate
    this.toDateValue = toDate

    // カレンダー要素の値を更新
    this.updateCalendarValue()
    this.updateDisplay()

    // ページ遷移が必要な場合のみ適用
    if (this.redirectPathValue) {
      this.applyDateRange()
    } else {
      this.setActivePreset(preset)
    }
  }

  // プリセット日付計算
  calculatePresetDates(preset) {
    const today = new Date()
    let fromDate, toDate
    
    switch (preset) {
      case 'last3days':
        // 直近3日間：3日前から今日まで
        fromDate = new Date(today)
        fromDate.setDate(today.getDate() - 2) // 今日を含む3日間なので-2
        toDate = new Date(today)
        break
        
      case 'last7days':
        // 直近7日間：7日前から今日まで
        fromDate = new Date(today)
        fromDate.setDate(today.getDate() - 6) // 今日を含む7日間なので-6
        toDate = new Date(today)
        break

      case 'last30days':
        // 直近30日間：30日前から今日まで
        fromDate = new Date(today)
        fromDate.setDate(today.getDate() - 29) // 今日を含む30日間なので-29
        toDate = new Date(today)
        break
        
      case 'thisMonth':
        // 今月：今月1日から今日まで
        fromDate = new Date(today.getFullYear(), today.getMonth(), 1)
        toDate = new Date(today)
        break
        
      case 'lastMonth':
        // 先月：先月1日から先月末日まで
        fromDate = new Date(today.getFullYear(), today.getMonth() - 1, 1)
        toDate = new Date(today.getFullYear(), today.getMonth(), 0) // 前月の最終日
        break
        
      default:
        // デフォルト：過去13日間
        fromDate = new Date(today)
        fromDate.setDate(today.getDate() - 12) // 今日を含む13日間なので-12
        toDate = new Date(today)
    }
    
    // 日付を YYYY-MM-DD 形式にフォーマット
    const formatDate = (date) => {
      const year = date.getFullYear()
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const day = date.getDate().toString().padStart(2, '0')
      return `${year}-${month}-${day}`
    }
    
    return {
      fromDate: formatDate(fromDate),
      toDate: formatDate(toDate)
    }
  }

  handleCalendarChange() {
    if (!this.hasElementTarget) return
    const value = this.elementTarget.value
    if (!value || !value.includes('/')) return

    const [fromDate, toDate] = value.split('/')
    this.fromDateValue = fromDate
    this.toDateValue = toDate
    this.highlightActiveOption()
  }

  activateCustomMode(event) {
    event.preventDefault()
    event.stopPropagation()
    this.setActivePreset(null)
    if (this.hasElementTarget) {
      this.elementTarget.focus()
    }
  }

  determineActivePreset() {
    if (!this.hasPresetButtonTarget) return null
    for (const button of this.presetButtonTargets) {
      const preset = button.dataset.preset
      const { fromDate, toDate } = this.calculatePresetDates(preset)
      if (fromDate === this.fromDateValue && toDate === this.toDateValue) {
        return preset
      }
    }
    return null
  }

  highlightActiveOption() {
    const activePreset = this.determineActivePreset()
    this.setActivePreset(activePreset)
  }

  setActivePreset(preset) {
    this.presetButtonTargets.forEach(button => {
      const isActive = button.dataset.preset === preset
      this.toggleActiveClasses(button, isActive)
    })

    if (this.hasCustomButtonTarget) {
      const highlightCustom = !preset
      this.toggleActiveClasses(this.customButtonTarget, highlightCustom, { baseClasses: ['btn', 'btn-ghost', 'btn-xs', 'rounded-full'] })
    }
  }

  toggleActiveClasses(element, active, options = {}) {
    if (!element) return

    const activeClasses = options.activeClasses || ['bg-primary/10', 'text-primary', 'font-semibold', 'border', 'border-primary/30']
    const inactiveClasses = options.inactiveClasses || ['bg-transparent', 'text-base-content/80', 'font-normal', 'border-transparent']

    activeClasses.forEach(cls => element.classList.toggle(cls, active))
    inactiveClasses.forEach(cls => element.classList.toggle(cls, !active))
  }

  setCalendarValue(value, triggerEvents) {
    this.elementTarget.setAttribute('value', value)
    this.elementTarget.value = value
    this.syncCalendarMonthsFromValue(value)
    if (triggerEvents) {
      this.elementTarget.dispatchEvent(new Event('input', { bubbles: true }))
      this.elementTarget.dispatchEvent(new Event('change', { bubbles: true }))
    }
  }

  syncCalendarMonthsFromValue(value) {
    if (!value || !value.includes('/')) return
    const [fromDate] = value.split('/')
    if (!fromDate) return

    const from = new Date(fromDate)
    if (Number.isNaN(from.getTime())) return

    const year = from.getFullYear()
    const month = String(from.getMonth() + 1).padStart(2, '0')
    const monthValue = `${year}-${month}`

    const calendarMonths = this.elementTarget.querySelectorAll('calendar-month')
    if (calendarMonths.length === 0) return

    calendarMonths[0].setAttribute('month', monthValue)
    if (calendarMonths.length > 1) {
      const next = new Date(from.getFullYear(), from.getMonth() + 1, 1)
      const nextYear = next.getFullYear()
      const nextMonth = String(next.getMonth() + 1).padStart(2, '0')
      calendarMonths[1].setAttribute('month', `${nextYear}-${nextMonth}`)
    }
  }

}
